import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Stack,
  Container,
  Badge,
  CircularProgress,
  Avatar,
  CardHeader,
  CardMedia,
  CardActions,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { GetTimeString } from "../../utiles";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExploreIcon from "@mui/icons-material/Explore";
import ForestIcon from "@mui/icons-material/Forest";
import EmojiNatureIcon from "@mui/icons-material/EmojiNature";
import DiamondIcon from "@mui/icons-material/Diamond";
import PetsIcon from "@mui/icons-material/Pets";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { claimExploration, Reload } from "../../store/user/actions";
import "../../pages/Tools.css"; // Reuse the wood-themed styling

// Helper function to claim all explorations
const claimAllExplorations =
  (exploreIds: number[]) => async (dispatch: any) => {
    try {
      const results = await Promise.all(
        exploreIds.map((id) => dispatch(claimExploration(id)))
      );

      return results.every((result) => result);
    } catch (error) {
      console.error("Claim all explorations error:", error);
      return false;
    }
  };

export const ClaimExplorations = () => {
  const dispatch = useDispatch<any>();
  const userModule = useSelector((state: AppState) => state.userModule);
  // Add null checks and provide default values
  const { loginInfo = {} as any, gameStatus = {} } = userModule || {};

  const [explorations, setExplorations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [claimableExplorations, setClaimableExplorations] = useState<number[]>(
    []
  );
  const [showInstructions, setShowInstructions] = useState(false);
  const [totalRewards, setTotalRewards] = useState({
    min: 0,
    max: 0,
    nftChance: 0
  });

  // URI prefix for NFT images
  const URI_PREFIX = "https://ipfs.io/ipfs/";

  // Fetch explorations from the explorations table
  const fetchExplorations = useCallback(async () => {
    setIsLoading(true);
    try {
      // Add null check for gameStatus
      const fetchedExplorations = gameStatus?.explorations || [];      
      setExplorations(fetchedExplorations);

      // Check which explorations are ready to claim
      const currentTime = Math.floor(Date.now() / 1000);
      const claimable = fetchedExplorations
        .filter((exp: any) => exp && exp.end_time <= currentTime)
        .map((exp: any) => exp.id);

      setClaimableExplorations(claimable);
      
      // Calculate total potential rewards across all claimable explorations
      let totalMin = 0;
      let totalMax = 0;
      let highestNftChance = 0;
      
      claimable.forEach((id: number) => {
        const exploration = fetchedExplorations.find((exp: any) => exp.id === id);
        if (exploration) {
          const worldInfo = gameStatus?.reward_info?.find(
            (w: any) => w && w.id === exploration.world_id
          );
          
          if (worldInfo) {
            const { minReward, maxReward } = calculateExpectedRewards(
              exploration,
              worldInfo
            );
            
            // Safely parse rewards, defaulting to 0 if NaN
            const minRewardNum = parseFloat(minReward) || 0;
            const maxRewardNum = parseFloat(maxReward) || 0;
            
            totalMin += minRewardNum;
            totalMax += maxRewardNum;
            
            // Track highest NFT chance
            if (worldInfo.nft_chance > highestNftChance) {
              highestNftChance = worldInfo.nft_chance;
            }
          }
        }
      });
      
      setTotalRewards({
        min: totalMin,
        max: totalMax,
        nftChance: highestNftChance
      });

      toast.success('Exploration data refreshed!');
      
    } catch (error) {
      console.error("Error fetching explorations:", error);
      toast.error("Failed to load explorations");
    } finally {
      setIsLoading(false);
    }
  }, [gameStatus]);

  useEffect(() => {
    fetchExplorations();
  }, [fetchExplorations]);

  // Handle claiming a single exploration
  const handleClaimExploration = async (exploreId: number) => {
    try {
      // Find the exploration and worldInfo only once, and guard against missing data
      const exploration = explorations.find((exp: any) => exp?.id === exploreId);
      if (!exploration) {
        toast.error(`Exploration #${exploreId} not found`);
        return;
      }
      const worldInfo = gameStatus?.reward_info?.find((w: any) => w?.id === exploration.world_id);

      const success = await dispatch(claimExploration(exploreId));
      if (success) {
        toast.success(`Successfully claimed exploration #${exploreId}`);
        if (parseInt(worldInfo.nft_chance) > 0)
          toast.info("Please check your account to see if you are one of the lucky NFT winners!");
        dispatch(Reload()); // Refresh data after claiming
      } else {
        toast.error(`Failed to claim exploration #${exploreId}`);
      }
    } catch (error) {
      console.error("Error claiming exploration:", error);
      toast.error("An error occurred while claiming");
    }
  };

  // Handle claiming all explorations
  const handleClaimAll = async () => {
    if (claimableExplorations.length === 0) {
      toast.info("No explorations ready to claim");
      return;
    }

    try {
      const success = await dispatch(
        claimAllExplorations(claimableExplorations)
      );
      if (success) {
        toast.success(
          `Successfully claimed ${claimableExplorations.length} explorations`
        );
        dispatch(Reload()); // Refresh user data
      } else {
        toast.error("Failed to claim some or all explorations");
      }
    } catch (error) {
      console.error("Error claiming all explorations:", error);
      toast.error("An error occurred while claiming");
    }
  };

  // Find NFT data by asset ID - add null checks
  const findNftData = (assetId: string) => {
    if (!loginInfo || !loginInfo.assets) return null;

    return (
      loginInfo.assets.find(
        (asset: any) => asset && String(asset.asset_id) === String(assetId)
      ) || null
    );
  };

  // Calculate expected rewards based on deer multipliers
  const calculateExpectedRewards = (exploration: any, worldInfo: any) => {
    if (!worldInfo || !exploration || !loginInfo?.assets) {
      return { minReward: "0.0000", maxReward: "0.0000" };
    }

    // Parse base min and max rewards from the world info
    const baseMinReward = parseFloat(worldInfo.min_reward || "0") || 0;
    const baseMaxReward = parseFloat(worldInfo.max_reward || "0") || 0;

    // Early return if rewards are not valid numbers
    if (isNaN(baseMinReward) || isNaN(baseMaxReward)) {
      console.warn(`Invalid reward values for world ${worldInfo.id}: min=${worldInfo.min_reward}, max=${worldInfo.max_reward}`);
      return { minReward: "0.0000", maxReward: "0.0000" };
    }

    let totalMinReward = 0;
    let totalMaxReward = 0;

    // Process each deer's reward multiplier individually, with null checks
    if (Array.isArray(exploration.asset_ids) && exploration.asset_ids.length > 0) {
      exploration.asset_ids.forEach((assetId: string) => {
        const nftData = findNftData(assetId);
        // Get the reward multiplier or default to 1 if not found
        let rewardMultiplier = nftData?.deer_config?.reward_multi || 1;
        
        // Ensure rewardMultiplier is a number
        if (typeof rewardMultiplier !== 'number') {
          rewardMultiplier = parseFloat(rewardMultiplier) || 1;
        }
        
        const deerLevelMulti = nftData?.deer_config?.level_multi || [];
        const level = nftData?.level || 1;
        
        // Apply level multiplier if exists for this level
        const levelMultiEntry = deerLevelMulti.find((a: any) => a && a.key == level);
        if (levelMultiEntry && typeof levelMultiEntry.value === 'number') {
          rewardMultiplier += levelMultiEntry.value / 100 - 1;
        }

        // Add this deer's contribution to the total
        totalMinReward += baseMinReward * rewardMultiplier;
        totalMaxReward += baseMaxReward * rewardMultiplier;
      });
    } else {
      // If no deer assets, use base rewards
      totalMinReward = baseMinReward;
      totalMaxReward = baseMaxReward;
    }

    // Ensure the values are not NaN before returning
    if (isNaN(totalMinReward) || isNaN(totalMaxReward)) {
      console.warn(`Calculated NaN rewards for exploration ${exploration.id}`, { 
        baseMin: baseMinReward, 
        baseMax: baseMaxReward,
        totalMin: totalMinReward,
        totalMax: totalMaxReward
      });
      return { minReward: "0.0000", maxReward: "0.0000" };
    }

    // Log reward-related data for debugging
    // console.log(`Exploration #${exploration.id} - World ${exploration.world_id}:`, {
    //   worldInfo,
    //   minReward: totalMinReward.toFixed(4),
    //   maxReward: totalMaxReward.toFixed(4),
    //   assetCount: exploration?.asset_ids?.length || 0
    // });

    return {
      minReward: totalMinReward.toFixed(4),
      maxReward: totalMaxReward.toFixed(4),
    };
  };

  // Instructions panel content with wood-themed styling
  const instructionsContent = (
    <Paper className="game-paper" sx={{ p: 3, mb: 3, position: 'relative', zIndex: 1 }}>
      <Typography variant="h5" gutterBottom sx={{ color: '#442a0c', fontWeight: 'bold' }}>
        How to Claim Explorations
      </Typography>
      <Divider sx={{ mb: 2, bgcolor: 'rgba(68, 42, 12, 0.2)' }} />
      <Typography variant="body1" paragraph sx={{ color: '#442a0c' }}>
        When your Deer NFTs return from exploration, you can claim your rewards here:
      </Typography>
      <ol style={{ paddingLeft: "1.5rem", color: '#442a0c' }}>
        <li>
          <Typography variant="body1" paragraph sx={{ color: '#442a0c' }}>
            Wait until the exploration timer reaches zero
          </Typography>
        </li>
        <li>
          <Typography variant="body1" paragraph sx={{ color: '#442a0c' }}>
            Click the "Claim" button for individual explorations or "Claim All"
            for all completed explorations
          </Typography>
        </li>
        <li>
          <Typography variant="body1" paragraph sx={{ color: '#442a0c' }}>
            Your ANTLERS rewards will be sent to your in-game balance
          </Typography>
        </li>
        <li>
          <Typography variant="body1" paragraph sx={{ color: '#442a0c' }}>
            If you're lucky, you might also receive special NFT drops!
          </Typography>
        </li>
      </ol>
      <Box sx={{ bgcolor: 'rgba(217, 83, 79, 0.1)', p: 2, borderRadius: '8px', border: '1px solid rgba(217, 83, 79, 0.3)', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: '#442a0c', fontWeight: 'bold' }}>
          Pro Tip:
        </Typography>
        <Typography variant="body2" sx={{ color: '#442a0c' }}>
          Different worlds offer varying rewards and NFT drop chances. Higher-level Deer have better reward multipliers, increasing your potential ANTLERS earnings!
        </Typography>
      </Box>
      <Button
        variant="contained"
        onClick={() => setShowInstructions(false)}
        sx={{ 
          bgcolor: '#3d2c0a', 
          color: 'white',
          '&:hover': { bgcolor: '#5c3c10' }
        }}
      >
        Got it!
      </Button>
    </Paper>
  );

  // Guard against undefined gameStatus
  const rewardInfo = gameStatus?.reward_info || [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 4, 
          background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
          borderRadius: '16px',
          position: 'relative', 
          overflow: 'hidden',
          border: '8px solid #5c3c10',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="white" sx={{ textShadow: '2px 2px 4px rgba(0,0,0,0.6)' }}>
              EXPLORATION REWARDS
            </Typography>
            <Typography variant="body1" color="rgba(255,255,255,0.9)" sx={{ mt: 1, textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
              Claim rewards from your Deer's adventures in the wilderness
            </Typography>
          </Box>
          
          <Tooltip title="View Instructions">
            <IconButton
              onClick={() => setShowInstructions(!showInstructions)}
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
              size="large"
            >
              <HelpOutlineIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Wood grain overlay */}
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
          opacity: 0.4, 
          zIndex: 1 
        }} />
        
        {/* Decorative elements */}
        <Box sx={{ 
          position: 'absolute', 
          right: {xs: -50, md: 50}, 
          top: 0, 
          opacity: 0.15, 
          zIndex: 1, 
          transform: 'rotate(-10deg)' 
        }}>
          <ForestIcon sx={{ fontSize: 150 }} />
        </Box>
      </Paper>

      {/* Instructions Panel (Togglable) */}
      {showInstructions && instructionsContent}

      {/* Stats Cards Row */}
      {!isLoading && explorations.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
              border: '4px solid #5c3c10',
              borderRadius: '12px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Wood grain pattern overlay */}
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                opacity: 0.3,
                zIndex: 0 
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ 
                    bgcolor: 'rgba(77, 124, 254, 0.2)', 
                    borderRadius: '12px', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ExploreIcon sx={{ fontSize: 36, color: '#4d7cfe' }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Total Explorations
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="white">
                      {explorations.length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
              border: '4px solid #5c3c10',
              borderRadius: '12px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Wood grain pattern overlay */}
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                opacity: 0.3,
                zIndex: 0 
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ 
                    bgcolor: 'rgba(46, 202, 106, 0.2)', 
                    borderRadius: '12px', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <PetsIcon sx={{ fontSize: 36, color: '#2eca6a' }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Active Deer
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="white">
                      {explorations.reduce((total: number, exp: any) => 
                        total + (exp?.asset_ids?.length || 0), 0)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
              border: '4px solid #5c3c10',
              borderRadius: '12px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Wood grain pattern overlay */}
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                opacity: 0.3,
                zIndex: 0 
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ 
                    bgcolor: 'rgba(255, 173, 70, 0.2)', 
                    borderRadius: '12px', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <EmojiNatureIcon sx={{ fontSize: 36, color: '#ffad46' }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Claimable Rewards
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="white">
                      {claimableExplorations.length}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              height: '100%', 
              background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
              border: '4px solid #5c3c10',
              borderRadius: '12px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Wood grain pattern overlay */}
              <Box sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                opacity: 0.3,
                zIndex: 0 
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ 
                    bgcolor: 'rgba(233, 76, 88, 0.2)', 
                    borderRadius: '12px', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <DiamondIcon sx={{ fontSize: 36, color: '#e94c58' }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      Potential ANTLERS
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="white">
                      {!isNaN(totalRewards.min) && !isNaN(totalRewards.max) 
                        ? `${totalRewards.min.toFixed(2)} - ${totalRewards.max.toFixed(2)}`
                        : "0.00 - 0.00"}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Main Content Container */}
      <Paper
        elevation={3}
        className="wood-panel"
        sx={{
          p: 3,
          borderRadius: '16px',
          position: 'relative',
          background: 'linear-gradient(135deg, #5c3c10 0%, #4a300b 100%)',
          border: '8px solid #5c3c10',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.3) 0px, rgba(92, 60, 16, 0.3) 2px, transparent 2px, transparent 4px)',
            opacity: 0.5,
            zIndex: 0
          },
          '& > *': {
            position: 'relative',
            zIndex: 1
          }
        }}
      >
        {/* Action Buttons */}
        <Grid container spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              fullWidth={false}
              disabled={claimableExplorations.length === 0}
              onClick={handleClaimAll}
              startIcon={<DiamondIcon />}
              sx={{
                backgroundColor: '#e94c58',
                color: 'white',
                '&:hover': { backgroundColor: '#d43545' },
                '&.Mui-disabled': { backgroundColor: 'rgba(233, 76, 88, 0.3)', color: 'rgba(255,255,255,0.5)' },
                borderRadius: '8px',
                px: 3,
                py: 1,
                fontWeight: 'bold'
              }}
            >
              Claim All Rewards ({claimableExplorations.length})
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchExplorations}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.05)' },
                borderRadius: '8px'
              }}
            >
              Refresh Explorations
            </Button>
          </Grid>
        </Grid>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ width: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress size={60} sx={{ color: '#e94c58' }} />
            <Typography variant="h6" color="white">Loading your explorations...</Typography>
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && explorations.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ForestIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.2)', mb: 3 }} />
            <Typography variant="h5" color="white" gutterBottom>
              No Active Explorations
            </Typography>
            <Typography variant="body1" color="rgba(255,255,255,0.7)" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
              You don't have any active or completed explorations. Visit the Explore page to send your Deer NFTs on adventure to earn rewards.
            </Typography>
            <Button
              variant="contained"
              href="/explore"
              sx={{ 
                backgroundColor: '#2eca6a', 
                '&:hover': { backgroundColor: '#25a65a' },
                borderRadius: '8px',
                px: 4,
                py: 1.5
              }}
            >
              Start Exploring
            </Button>
          </Box>
        )}

        {/* Explorations Grid */}
        {!isLoading && explorations.length > 0 && (
          <Grid container spacing={3}>
            {Array.isArray(explorations) &&
              explorations.map((explore) => {
                if (!explore) return null;

                const currentTime = Math.floor(Date.now() / 1000);
                const isCompleted = explore.end_time <= currentTime;
                const timeRemaining = Math.max(0, explore.end_time - currentTime);
                const worldInfo = rewardInfo.find(
                  (w: any) => w && w.id === explore.world_id
                );

                // Calculate expected rewards with deer multipliers
                const { minReward, maxReward } = calculateExpectedRewards(
                  explore,
                  worldInfo
                );
                
                // Calculate progress percentage
                const progressPercent = Math.min(
                  100,
                  ((explore.end_time - explore.start_time - timeRemaining) /
                    (explore.end_time - explore.start_time)) *
                  100
                );

                // Log reward-related data for debugging
                // console.log(`Exploration #${explore.id} - World ${explore.world_id}:`, {
                //   worldInfo,
                //   minReward,
                //   maxReward,
                //   assetCount: explore?.asset_ids?.length || 0
                // });

                return (
                  <Grid item xs={12} sm={6} md={4} key={explore.id}>
                    <Card
                      sx={{
                        background: isCompleted
                          ? 'linear-gradient(135deg, #3d4a0b 0%, #2c3d0a 100%)'
                          : 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
                        border: isCompleted
                          ? '4px solid #2eca6a'
                          : '4px solid #ffad46',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        position: 'relative',
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.4)'
                        },
                      }}
                    >
                      {/* Wood grain pattern overlay */}
                      <Box sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                        opacity: 0.3,
                        zIndex: 0 
                      }} />
                      
                      <CardHeader
                        title={
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="h6" color="white" sx={{ fontWeight: 'bold' }}>
                              Exploration #{explore.id}
                            </Typography>
                            <Chip
                              label={isCompleted ? "Ready to Claim" : "In Progress"}
                              color={isCompleted ? "success" : "warning"}
                              size="small"
                              sx={{ fontWeight: 'medium' }}
                            />
                          </Box>
                        }
                        sx={{ position: 'relative', zIndex: 1, pb: 0 }}
                      />
                      
                      <CardContent sx={{ position: 'relative', zIndex: 1, pt: 1 }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", bgcolor: 'rgba(255,255,255,0.1)', px: 1, py: 0.5, borderRadius: '4px' }}>
                            <PublicIcon
                              sx={{ fontSize: 16, color: "white", mr: 0.5 }}
                            />
                            <Typography variant="body2" color="white">
                              World {explore.world_id}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", bgcolor: 'rgba(255,255,255,0.1)', px: 1, py: 0.5, borderRadius: '4px' }}>
                            <PersonIcon
                              sx={{ fontSize: 16, color: "white", mr: 0.5 }}
                            />
                            <Typography variant="body2" color="white" noWrap sx={{ maxWidth: '120px' }}>
                              {explore.owner}
                            </Typography>
                          </Box>
                        </Stack>

                        <Divider
                          sx={{ my: 1.5, bgcolor: "rgba(255, 255, 255, 0.2)" }}
                        />

                        {/* Reward Preview - Updated to use calculated values based on multipliers */}
                        {worldInfo ? (
                          <Paper sx={{ p: 1.5, mb: 2, background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="white" fontWeight="medium">
                                Expected Reward:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DiamondIcon sx={{ color: '#ffcc33', fontSize: 20 }} />
                                <Typography variant="body1" color="#ffcc33" fontWeight="bold">
                                  {!isNaN(parseFloat(minReward)) && !isNaN(parseFloat(maxReward))
                                    ? `${minReward} - ${maxReward} ANTLERS`
                                    : "0.0000 - 0.0000 ANTLERS"}
                                </Typography>
                              </Box>
                              {worldInfo.nft_chance > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <EmojiNatureIcon sx={{ color: '#66ffcc', fontSize: 20 }} />
                                  <Typography variant="body2" color="#66ffcc">
                                    NFT Drop Chance: {worldInfo.nft_chance / 100}%
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Paper>
                        ) : (
                          <Paper sx={{ p: 1.5, mb: 2, background: 'rgba(0,0,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <Stack spacing={1}>
                              <Typography variant="body2" color="white" fontWeight="medium">
                                World Information Unavailable
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DiamondIcon sx={{ color: '#ffcc33', fontSize: 20 }} />
                                <Typography variant="body1" color="#ffcc33" fontWeight="bold">
                                  Reward data not available
                                </Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        )}

                        {/* Deer NFTs with reward multipliers */}
                        <Typography variant="body2" color="white" fontWeight="medium" sx={{ mb: 1 }}>
                          Exploring Deer ({explore.asset_ids?.length || 0}):
                        </Typography>

                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}
                        >
                          {Array.isArray(explore.asset_ids) &&
                            explore.asset_ids.map((assetId: string) => {
                              const nftData = findNftData(assetId);
                              var rewardMultiplier =
                                nftData?.deer_config?.reward_multi || 1;

                              const extra_multi =
                                nftData?.deer_config?.level_multi || [];
                              const level = nftData?.level || 1;
                              if (extra_multi.find((a: any) => a.key == level)) {
                                rewardMultiplier +=
                                  extra_multi.find((a: any) => a.key == level)
                                    .value /
                                    100 -
                                  1;
                              }

                              return (
                                <Tooltip
                                  key={assetId}
                                  title={
                                    nftData
                                      ? `${
                                          nftData.data?.name || "Unknown"
                                        } (Level ${
                                          nftData.level || "?"
                                        }, ${parseFloat(rewardMultiplier)?.toFixed(2)}x Rewards)`
                                      : `Asset #${assetId}`
                                  }
                                >
                                  <Box sx={{ position: "relative" }}>
                                    <Avatar
                                      src={
                                        nftData && nftData.data?.img
                                          ? `${URI_PREFIX}${nftData.data.img}`
                                          : "/images/placeholder.png"
                                      }
                                      alt={
                                        nftData && nftData.data?.name
                                          ? nftData.data.name
                                          : `Asset #${assetId}`
                                      }
                                      sx={{
                                        width: 45,
                                        height: 45,
                                        border: '2px solid #5c3c10',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                      }}
                                    />
                                    {rewardMultiplier > 1 && (
                                      <Chip
                                        label={`${parseFloat(rewardMultiplier)?.toFixed(1)}x`}
                                        size="small"
                                        color="secondary"
                                        sx={{
                                          position: "absolute",
                                          top: -6,
                                          right: -6,
                                          height: 20,
                                          fontSize: "0.6rem",
                                          fontWeight: 'bold',
                                          px: 0.5,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Tooltip>
                              );
                            })}
                        </Box>

                        {/* Progress or Claim Button */}
                        {isCompleted ? (
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleClaimExploration(explore.id)}
                            startIcon={<DiamondIcon />}
                            sx={{
                              backgroundColor: '#2eca6a',
                              color: 'white',
                              '&:hover': { backgroundColor: '#25a65a' },
                              borderRadius: '8px',
                              py: 1,
                              fontWeight: 'bold',
                              mt: 1
                            }}
                          >
                            Claim Rewards
                          </Button>
                        ) : (
                          <Box sx={{ width: "100%" }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography
                                variant="body2"
                                color="white"
                                sx={{ fontWeight: 'medium' }}
                              >
                                Completing in:
                              </Typography>
                              <Typography
                                variant="body2"
                                color="#ffad46"
                                sx={{ fontWeight: 'bold' }}
                              >
                                {GetTimeString(timeRemaining)}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progressPercent}
                              sx={{ 
                                height: 10, 
                                borderRadius: 5, 
                                bgcolor: 'rgba(255,255,255,0.1)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: '#ffad46',
                                  borderRadius: 5
                                }
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="rgba(255,255,255,0.7)"
                              align="right"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {Math.round(progressPercent)}% Complete
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default ClaimExplorations;
